/**
 * ExpiryScreen — stock batches expired or nearing expiry, and the value at risk
 * of write-off. Loss prevention for perishable inventory.
 *
 * Computed entirely from local WatermelonDB — no network required.
 */

import { useCallback, useEffect, useState } from 'react';
import { Download, RefreshCw } from 'lucide-react';
import { useAppStore } from '@/store/useAppStore';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import { toDateInputValue } from '@/utils/dateRange';
import { Button } from '@/components/ui/Button';
import { pdf, file } from '@/platform';
import { toCsv } from '@/utils/csv';
import { loadExpiry } from '@/reports/expiry/expiryDataSource';
import { buildExpiry, type ExpiryReport, type ExpiryItem } from '@/reports/expiry/expiry';

// ─── Component ───────────────────────────────────────────────────────────────

export function ExpiryScreen() {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const [data, setData]       = useState<ExpiryReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    if (!activeStoreId) return;
    setLoading(true);
    try {
      const source = await loadExpiry(activeStoreId, 30, 90, new Date());
      setData(buildExpiry(source));
    } finally {
      setLoading(false);
    }
  }, [activeStoreId]);

  useEffect(() => { void load(); }, [load]);

  const atRisk = data ? data.items.filter((i) => i.bucket !== 'LATER') : [];

  const handleExportCsv = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const csv = toCsv(data.items, [
        { header: 'Product', value: (r) => r.productName },
        { header: 'Batch', value: (r) => r.batchNumber },
        { header: 'Category', value: (r) => r.category },
        { header: 'Expiry', value: (r) => toDateInputValue(r.expiryDate) },
        { header: 'Days to expiry', value: (r) => r.daysToExpiry },
        { header: 'Status', value: (r) => r.bucket },
        { header: 'Quantity', value: (r) => r.quantity },
        { header: 'Value at risk', value: (r) => r.valueAtRisk },
      ]);
      await file.saveText({
        content: csv,
        defaultFileName: `Expiry_${toDateInputValue(new Date())}.csv`,
        filters: [{ name: 'CSV', extensions: ['csv'] }],
      });
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!data) return;
    setExporting(true);
    try {
      await pdf.saveFromHtml({
        html: buildExpiryHtml(data, atRisk),
        defaultFileName: `Expiry_${toDateInputValue(new Date())}.pdf`,
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto pb-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-body text-fg-tertiary">As of {new Date().toLocaleDateString('en-IN')}</span>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={14} className={cn(loading && 'animate-spin')} />
          </Button>
          <Button variant="secondary" onClick={() => void handleExportCsv()} disabled={!data || exporting || data.items.length === 0}>
            <Download size={14} className="mr-1.5" /> CSV
          </Button>
          <Button variant="secondary" onClick={() => void handleExportPdf()} disabled={!data || exporting || atRisk.length === 0}>
            <Download size={14} className="mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center text-body text-fg-tertiary">Loading…</div>
      ) : !data ? null : data.items.length === 0 ? (
        <div className="mt-16 text-center text-body text-fg-tertiary">
          No batch-tracked stock with expiry dates to monitor.
        </div>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KPI label="Expired" value={formatCurrency(data.expired.value)} sub={`${data.expired.count} batches`} warn={data.expired.value > 0} />
            <KPI label={`≤ ${data.nearWindowDays} days`} value={formatCurrency(data.near.value)} sub={`${data.near.count} batches`} warn={data.near.value > 0} />
            <KPI label={`≤ ${data.soonWindowDays} days`} value={formatCurrency(data.soon.value)} sub={`${data.soon.count} batches`} />
            <KPI label="Total at risk" value={formatCurrency(data.totalAtRisk)} accent />
          </div>

          {atRisk.length === 0 ? (
            <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-8 text-center text-body text-fg-tertiary">
              Nothing expired or expiring within {data.soonWindowDays} days.
            </div>
          ) : (
            <Card title={`Expired & expiring within ${data.soonWindowDays} days`}>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-body">
                  <thead>
                    <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                      <th className="pb-1 text-left font-normal">Product</th>
                      <th className="pb-1 text-left font-normal">Batch</th>
                      <th className="pb-1 text-center font-normal">Status</th>
                      <th className="pb-1 text-right font-normal">Expiry</th>
                      <th className="pb-1 text-right font-normal">Qty</th>
                      <th className="pb-1 text-right font-normal">Value at risk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {atRisk.map((it) => (
                      <tr key={`${it.productId}-${it.batchNumber}`} className="border-b border-stroke-hairline/10">
                        <td className="py-1.5">
                          <div className="text-fg-primary">{it.productName}</div>
                          <div className="text-label text-fg-tertiary">{it.category}</div>
                        </td>
                        <td className="py-1.5 text-fg-secondary tabular-nums">{it.batchNumber}</td>
                        <td className="py-1.5 text-center"><StatusBadge item={it} /></td>
                        <td className="py-1.5 text-right tabular-nums text-fg-secondary">
                          {it.expiryDate.toLocaleDateString('en-IN')}
                          <div className="text-label text-fg-tertiary">
                            {it.daysToExpiry < 0 ? `${-it.daysToExpiry}d ago` : `in ${it.daysToExpiry}d`}
                          </div>
                        </td>
                        <td className="py-1.5 text-right tabular-nums text-fg-secondary">{it.quantity}</td>
                        <td className={cn('py-1.5 text-right font-medium tabular-nums',
                          it.bucket === 'EXPIRED' ? 'text-warning-ink' : 'text-fg-primary')}>
                          {formatCurrency(it.valueAtRisk)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────────

function StatusBadge({ item }: { item: ExpiryItem }) {
  const label = item.bucket === 'EXPIRED' ? 'expired' : item.bucket === 'NEAR' ? 'near' : 'soon';
  const cls = item.bucket === 'EXPIRED' ? 'bg-warning/15 text-warning-ink'
    : item.bucket === 'NEAR' ? 'bg-warning/10 text-warning-ink' : 'bg-accent/10 text-accent-ink';
  return <span className={cn('rounded px-1.5 py-0.5 text-label', cls)}>{label}</span>;
}

function KPI({ label, value, sub, accent, warn }: { label: string; value: string; sub?: string; accent?: boolean; warn?: boolean }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="text-label uppercase text-fg-tertiary">{label}</div>
      <div className={cn('mt-1 text-heading tabular-nums', warn ? 'text-warning-ink' : accent ? 'text-accent-ink' : 'text-fg-primary')}>
        {value}
      </div>
      {sub && <div className="mt-0.5 text-label text-fg-tertiary">{sub}</div>}
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="mb-2 text-label uppercase text-fg-tertiary">{title}</div>
      {children}
    </div>
  );
}

// ─── PDF export ───────────────────────────────────────────────────────────────

function buildExpiryHtml(d: ExpiryReport, atRisk: ExpiryItem[]): string {
  const fmt = (n: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n);
  const esc = (s: string) => s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c] as string);

  const rows = atRisk.map((it) =>
    `<tr><td>${esc(it.productName)}</td><td>${esc(it.batchNumber)}</td>
     <td>${it.bucket.toLowerCase()}</td>
     <td style="text-align:right">${it.expiryDate.toLocaleDateString('en-IN')}</td>
     <td style="text-align:right">${it.daysToExpiry < 0 ? -it.daysToExpiry + 'd ago' : 'in ' + it.daysToExpiry + 'd'}</td>
     <td style="text-align:right">${it.quantity}</td>
     <td style="text-align:right${it.bucket === 'EXPIRED' ? ';color:#b45309' : ''}">${fmt(it.valueAtRisk)}</td></tr>`,
  ).join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>body{font-family:sans-serif;margin:32px;color:#222}h1{font-size:22px;margin-bottom:2px}
table{width:100%;border-collapse:collapse;font-size:11px;margin-top:10px}th,td{padding:5px 7px;border-bottom:1px solid #eee}
th{text-align:right;color:#666;font-weight:500}th:first-child,td:first-child,th:nth-child(2),td:nth-child(2),th:nth-child(3),td:nth-child(3){text-align:left}</style></head>
<body>
<h1>Expiry Report</h1>
<p style="color:#888">As of ${new Date().toLocaleDateString('en-IN')} · Expired ${fmt(d.expired.value)} (${d.expired.count}) · ≤${d.nearWindowDays}d ${fmt(d.near.value)} (${d.near.count}) · ≤${d.soonWindowDays}d ${fmt(d.soon.value)} (${d.soon.count}) · At risk ${fmt(d.totalAtRisk)}</p>
<table>
<thead><tr><th>Product</th><th>Batch</th><th>Status</th><th>Expiry</th><th>When</th><th>Qty</th><th>Value at risk</th></tr></thead>
<tbody>${rows}</tbody>
</table>
<p style="margin-top:36px;font-size:11px;color:#aaa">Generated by BigxBook Desktop on ${new Date().toLocaleString('en-IN')}</p>
</body></html>`;
}
